import Phaser from "phaser";
import DailyChallengeManager from "../game/DailyChallengeManager";
import { FONT_SIZE, fontStyle } from "../game/FontScale";
import { fitText, isNarrow } from "../game/view/layout";
import XAuth from "../game/XAuth";
import { addMuteButton } from "../game/MuteButton";

/**
 * DailyChallengeScene — lobby + results for daily challenges.
 *
 * Flow:
 *   1. Fetch today's challenge (difficulty, seed)
 *   2. Show lobby with challenge info + PLAY button
 *   3. Start StageScene in daily mode (free, no INSERT COIN)
 *   4. StageScene returns here with { stageResult, practice }; the scored
 *      attempt is submitted, then the results overlay shows rank, streak, share
 *
 * StageScene checks data.daily to:
 *   - Skip payment gate
 *   - Skip epoch scoring
 *   - Return here on completion instead of showing payment
 */

const EDGE_MARGIN = 16; // total horizontal margin for centred lines
const COL_GAP = 8; // minimum gap between table columns

/** Leaderboard column x positions as fractions of the canvas width. */
const LB_COLS_WIDE = { rank: 0.125, name: 0.2, score: 0.625, time: 0.8 };
const LB_COLS_NARROW = { rank: 0.03, name: 0.13, score: 0.59, time: 0.85 };

/** Outcome label colours on the results overlay. */
const OUTCOME_COLORS = { CLEARED: "#7BC74A", FLOODED: "#3FA9F5", DRAINED: "#FF00AA", "KNOCKED OUT": "#FF4444" };

const formatTime = (seconds) => `${(Number(seconds) || 0).toFixed(1)}s`;

class DailyChallengeScene extends Phaser.Scene {
  constructor() {
    super({ key: "DailyChallengeScene" });
  }

  async create() {
    this.cameras.main.setBackgroundColor("#0a0a0a");

    const W = this.scale.width;
    const H = this.scale.height;
    const CX = W / 2;
    this._W = W;
    this._H = H;
    this._CX = CX;

    // Returning from the daily stage? Consume the data so a later
    // scene.start()/restart() without data can't show or submit it again.
    const returnData = this.scene.settings.data || {};
    this.scene.settings.data = {};
    if (returnData.stageResult) {
      await this._finishStage(returnData.stageResult, Boolean(returnData.practice));
      return;
    }
    if (returnData.dailyResult) {
      this._showResults(returnData.dailyResult);
      return;
    }

    // Loading state
    const loadingText = this._fit(
      this.add
        .text(
          CX,
          H / 2,
          "LOADING DAILY CHALLENGE...",
          fontStyle(FONT_SIZE.BODY, "#666666"),
        )
        .setOrigin(0.5),
    );

    // Fetch today's challenge
    const challenge = await DailyChallengeManager.fetchChallenge();
    loadingText.destroy();

    this.challenge = challenge;
    this._showLobby(challenge);
  }

  /** Shrink a text to fit `maxWidth` (defaults to the canvas minus margins). */
  _fit(text, maxWidth = this._W - EDGE_MARGIN) {
    return fitText(text, maxWidth);
  }

  /** Fit a table cell: shrink to the 8px floor, then truncate with "..". */
  _fitCell(text, maxWidth) {
    fitText(text, maxWidth);
    let str = text.text;
    while (text.width > maxWidth && str.length > 1) {
      str = str.slice(0, -1);
      text.setText(`${str}..`);
    }
    return text;
  }

  // ── Lobby Screen ────────────────────────────────────────────

  _showLobby(challenge) {
    const H = this._H;
    const CX = this._CX;

    const alreadyPlayed = DailyChallengeManager.hasPlayedToday();
    const todayResult = DailyChallengeManager.getTodayResult();
    const streak = DailyChallengeManager.getStreak();

    // Title
    this._fit(
      this.add
        .text(
          CX,
          40,
          "DAILY CHALLENGE",
          fontStyle(FONT_SIZE.HEADING, "#FFD700"),
        )
        .setOrigin(0.5),
    );

    // Date
    this._fit(
      this.add
        .text(CX, 68, challenge.date, fontStyle(FONT_SIZE.CAPTION, "#666666"))
        .setOrigin(0.5),
    );

    // Difficulty badge
    const diffColors = {
      easy: "#27AE60",
      medium: "#F39C12",
      hard: "#E74C3C",
      extreme: "#8E44AD",
    };
    const diffColor = diffColors[challenge.difficulty] || "#FFFFFF";

    this._fit(
      this.add
        .text(
          CX,
          90,
          challenge.difficulty.toUpperCase(),
          fontStyle(FONT_SIZE.SUBHEADING, diffColor),
        )
        .setOrigin(0.5),
    );

    // Today's stage: the same Dams to AGI stage for everyone
    this._fit(
      this.add.text(CX, 130, "TODAY'S STAGE", fontStyle(FONT_SIZE.CAPTION, "#8B5E3C")).setOrigin(0.5),
    );
    this._fit(
      this.add.text(CX, 148, "SOLARPUNK BUILD", fontStyle(FONT_SIZE.BODY, "#7BC74A")).setOrigin(0.5),
    );
    this._fit(
      this.add.text(CX, 168, "SAME SEED FOR EVERY PLAYER", fontStyle(FONT_SIZE.CAPTION, "#999999")).setOrigin(0.5),
    );

    // Streak display
    if (streak && streak.currentStreak >= 3) {
      this._fit(
        this.add
          .text(
            CX,
            200,
            `STREAK: ${streak.currentStreak} DAYS`,
            fontStyle(FONT_SIZE.CAPTION, "#FF6600"),
          )
          .setOrigin(0.5),
      );

    }

    // Rules
    this._fit(
      this.add
        .text(
          CX,
          250,
          [
            "FREE TO PLAY",
            "SAME STAGE FOR ALL PLAYERS",
            alreadyPlayed
              ? "SCORED ATTEMPT USED  -  PRACTICE MODE"
              : "ONE SCORED ATTEMPT  -  UNLIMITED PRACTICE",
            "SCORES DO NOT COUNT TOWARD EPOCH",
          ].join("\n"),
          fontStyle(FONT_SIZE.CAPTION, "#999999", {
            align: "center",
            lineSpacing: 8,
          }),
        )
        .setOrigin(0.5),
    );

    // Already played — show previous result
    if (alreadyPlayed && todayResult) {
      this._fit(
        this.add
          .text(
            CX,
            330,
            `YOUR WOOD: ${(todayResult.score || 0).toLocaleString()}`,
            fontStyle(FONT_SIZE.BODY, "#7BC74A"),
          )
          .setOrigin(0.5),
      );

      if (todayResult.rank) {
        this._fit(
          this.add
            .text(
              CX,
              350,
              `RANK #${todayResult.rank} OF ${todayResult.totalPlayers}`,
              fontStyle(FONT_SIZE.CAPTION, "#00FFFF"),
            )
            .setOrigin(0.5),
        );
      }
    }

    // PLAY button
    const playY = alreadyPlayed ? 400 : 370;
    const playLabel = alreadyPlayed ? "PRACTICE" : "PLAY";
    const playBtn = this._fit(
      this.add
        .text(
          CX,
          playY,
          playLabel,
          fontStyle(FONT_SIZE.HEADING, "#7BC74A", {
            stroke: "#000000",
            strokeThickness: 3,
          }),
        )
        .setOrigin(0.5),
    ).setInteractive({ useHandCursor: true });

    this.tweens.add({
      targets: playBtn,
      alpha: 0.3,
      duration: 600,
      yoyo: true,
      repeat: -1,
    });

    playBtn.on("pointerdown", () => {
      this._startChallenge(challenge, alreadyPlayed);
    });

    // Leaderboard button
    this._fit(
      this.add
        .text(
          CX,
          playY + 50,
          "VIEW LEADERBOARD",
          fontStyle(FONT_SIZE.CAPTION, "#00FFFF"),
        )
        .setOrigin(0.5),
    )
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", () => this._showLeaderboard());

    // Back button
    this._fit(
      this.add
        .text(CX, H - 40, "BACK TO MENU", fontStyle(FONT_SIZE.BODY, "#8B5E3C"))
        .setOrigin(0.5),
    )
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", () => this.scene.start("MenuScene"));

    this.input.keyboard.on("keydown-ESC", () => this.scene.start("MenuScene"));

    addMuteButton(this);
  }

  // ── Start Challenge ─────────────────────────────────────────

  _startChallenge(challenge, isPractice) {
    // Everyone plays the same Dams to AGI stage on today's seed
    this.scene.start("StageScene", { level: 1, seed: challenge.seed, daily: true, dailyPractice: isPractice });
  }


  // ── Stage Finished ──────────────────────────────────────────

  /**
   * Submit a finished daily stage (unless practice) and show the results.
   * @param {Object} stageResult - StageSim result()
   * @param {boolean} practice
   */
  async _finishStage(stageResult, practice) {
    const summary = DailyChallengeManager.summarizeStage(stageResult);
    if (practice) {
      this._showResults({ ...summary, isPractice: true });
      return;
    }

    const sending = this._fit(
      this.add
        .text(this._CX, this._H / 2, "SUBMITTING...", fontStyle(FONT_SIZE.BODY, "#666666"))
        .setOrigin(0.5),
    );
    const submitted = await DailyChallengeManager.submitScore(stageResult);
    sending.destroy();

    if (submitted.error) {
      this._showResults({ ...summary, notice: this._submitNotice(submitted) });
      return;
    }
    this._showResults(submitted);
  }

  /** Short arcade line for a submission that did not rank. */
  _submitNotice(submitted) {
    if (submitted.alreadyPlayed) return "ALREADY SUBMITTED TODAY - NOT RANKED";
    if (!XAuth.getSessionToken()) return "SIGN IN WITH X TO RANK";
    return "COULD NOT SUBMIT - NOT RANKED";
  }

  // ── Results Overlay ─────────────────────────────────────────

  _showResults(result) {
    const W = this._W;
    const CX = this._CX;


    // Title
    this._fit(
      this.add
        .text(
          CX,
          30,
          result.isPractice ? "PRACTICE" : "CHALLENGE COMPLETE!",
          fontStyle(FONT_SIZE.HEADING, "#FFD700"),
        )
        .setOrigin(0.5),
    );

    this._fit(
      this.add.text(CX, 60, "DAMS TO AGI", fontStyle(FONT_SIZE.SUBHEADING, "#F5E6C8")).setOrigin(0.5),
    );

    // WOOD (the score)
    this._fit(
      this.add
        .text(CX, 110, "WOOD", fontStyle(FONT_SIZE.CAPTION, "#8B5E3C"))
        .setOrigin(0.5),
    );

    this._fit(
      this.add
        .text(
          CX,
          135,
          `${(result.score || 0).toLocaleString()}`,
          fontStyle(FONT_SIZE.HEADING, "#7BC74A"),
        )
        .setOrigin(0.5),
    );

    // Stats grid
    const stats = [
      { label: "ENERGY", value: `${Math.round(result.totalWh || 0).toLocaleString()} Wh` },
      { label: "TIME", value: formatTime(result.timeSeconds) },
      { label: "RESULT", value: result.outcome || "-", color: OUTCOME_COLORS[result.outcome] },
    ];

    const statSpacing = W * 0.225;
    const statMaxW = statSpacing - COL_GAP;
    stats.forEach((s, i) => {
      const x = CX - statSpacing + i * statSpacing;
      this._fit(
        this.add
          .text(x, 180, s.label, fontStyle(FONT_SIZE.CAPTION, "#666666"))
          .setOrigin(0.5),
        statMaxW,
      );
      this._fit(
        this.add
          .text(x, 200, s.value, fontStyle(FONT_SIZE.SUBHEADING, s.color || "#FFFFFF"))
          .setOrigin(0.5),
        statMaxW,
      );
    });

    // Rank (if scored attempt)
    if (result.rank) {
      this._fit(
        this.add
          .text(
            CX,
            250,
            `RANK #${result.rank} OF ${result.totalPlayers}`,
            fontStyle(FONT_SIZE.SUBHEADING, "#00FFFF"),
          )
          .setOrigin(0.5),
      );
    } else if (result.isPractice || result.notice) {
      this._fit(
        this.add
          .text(
            CX,
            250,
            result.notice || "PRACTICE MODE - NOT RANKED",
            fontStyle(FONT_SIZE.CAPTION, "#666666"),
          )
          .setOrigin(0.5),
      );
    }

    // Streak
    if (result.streakInfo && result.streakInfo.currentStreak >= 2) {
      const streakColor =
        result.streakInfo.currentStreak >= 7 ? "#FF6600" : "#FFD700";
      this._fit(
        this.add
          .text(
            CX,
            290,
            `STREAK: ${result.streakInfo.currentStreak} DAYS`,
            fontStyle(FONT_SIZE.BODY, streakColor),
          )
          .setOrigin(0.5),
      );
    }

    // Share button
    const xUser = XAuth.getUser();
    if (xUser) {
      const shareBtn = this._fit(
        this.add
          .text(
            CX,
            350,
            "SHARE ON X",
            fontStyle(FONT_SIZE.BODY, "#FFFFFF", {
              stroke: "#777777",
              strokeThickness: 2,
            }),
          )
          .setOrigin(0.5),
      ).setInteractive({ useHandCursor: true });

      shareBtn.on("pointerdown", () => {
        const shareText = DailyChallengeManager.generateShareText(result);

        // Copy to clipboard
        if (navigator.clipboard) {
          navigator.clipboard.writeText(shareText);
        }

        // Also open X share
        XAuth.shareText(shareText);
      });
    }

    // Copy to clipboard button
    const copyBtn = this._fit(
      this.add
        .text(CX, 385, "COPY RESULT", fontStyle(FONT_SIZE.CAPTION, "#8B5E3C"))
        .setOrigin(0.5),
    ).setInteractive({ useHandCursor: true });

    copyBtn.on("pointerdown", () => {
      const shareText = DailyChallengeManager.generateShareText(result);
      if (navigator.clipboard) {
        navigator.clipboard.writeText(shareText);
        copyBtn.setText("COPIED!");
        this.time.delayedCall(1500, () => copyBtn.setText("COPY RESULT"));
      }
    });

    // Play again (practice) + Back to menu
    const btnOffset = isNarrow(this) ? W / 4 : W * 0.125;
    const btnMaxW = btnOffset * 2 - EDGE_MARGIN;
    this._fit(
      this.add
        .text(
          CX - btnOffset,
          450,
          "PLAY AGAIN",
          fontStyle(FONT_SIZE.BODY, "#7BC74A"),
        )
        .setOrigin(0.5),
      btnMaxW,
    )
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", () => {
        this.scene.restart(); // Re-enters DailyChallengeScene (lobby)
      });

    this._fit(
      this.add
        .text(
          CX + btnOffset,
          450,
          "LEADERBOARD",
          fontStyle(FONT_SIZE.BODY, "#00FFFF"),
        )
        .setOrigin(0.5),
      btnMaxW,
    )
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", () => this._showLeaderboard());

    this._fit(
      this.add
        .text(CX, 520, "BACK TO MENU", fontStyle(FONT_SIZE.BODY, "#8B5E3C"))
        .setOrigin(0.5),
    )
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", () => this.scene.start("MenuScene"));

    this.input.keyboard.on("keydown-ESC", () => this.scene.start("MenuScene"));

    addMuteButton(this);
  }

  // ── Leaderboard Overlay ─────────────────────────────────────

  async _showLeaderboard() {
    const W = this._W;
    const H = this._H;
    const CX = this._CX;

    if (this.lbGroup) this.lbGroup.destroy(true);
    this.lbGroup = this.add.group();

    // Overlay
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.9);
    bg.fillRect(0, 0, W, H);
    this.lbGroup.add(bg);

    const closeZone = this.add.zone(0, 0, W, H).setOrigin(0).setInteractive();
    this.lbGroup.add(closeZone);

    const title = this._fit(
      this.add
        .text(
          CX,
          30,
          "DAILY LEADERBOARD",
          fontStyle(FONT_SIZE.SUBHEADING, "#FFD700"),
        )
        .setOrigin(0.5),
    );
    this.lbGroup.add(title);

    const loadText = this._fit(
      this.add
        .text(CX, H / 2, "LOADING...", fontStyle(FONT_SIZE.BODY, "#666666"))
        .setOrigin(0.5),
    );
    this.lbGroup.add(loadText);

    const data = await DailyChallengeManager.fetchLeaderboard();
    loadText.destroy();

    const dateText = this._fit(
      this.add
        .text(CX, 50, data.date, fontStyle(FONT_SIZE.CAPTION, "#666666"))
        .setOrigin(0.5),
    );
    this.lbGroup.add(dateText);

    const playersText = this._fit(
      this.add
        .text(
          CX,
          65,
          `${data.totalPlayers} PLAYERS`,
          fontStyle(FONT_SIZE.CAPTION, "#999999"),
        )
        .setOrigin(0.5),
    );
    this.lbGroup.add(playersText);

    // Leaderboard entries
    const medalColors = ["#FFD700", "#C0C0C0", "#CD7F32"];
    const startY = 100;

    if (data.leaderboard.length === 0) {
      const emptyText = this._fit(
        this.add
          .text(
            CX,
            200,
            "NO SCORES YET TODAY",
            fontStyle(FONT_SIZE.BODY, "#777777"),
          )
          .setOrigin(0.5),
      );
      this.lbGroup.add(emptyText);
    }

    const colFrac = isNarrow(this) ? LB_COLS_NARROW : LB_COLS_WIDE;
    const col = {
      rank: W * colFrac.rank,
      name: W * colFrac.name,
      score: W * colFrac.score,
      time: W * colFrac.time,
    };
    const colW = {
      rank: col.name - col.rank - COL_GAP,
      name: col.score - col.name - COL_GAP,
      score: col.time - col.score - COL_GAP,
      time: W - EDGE_MARGIN / 2 - col.time,
    };

    if (data.leaderboard.length > 0) {
      const headers = [
        { key: "score", str: "WOOD" },
        { key: "time", str: "TIME" },
      ];
      headers.forEach((h) => {
        const cell = this._fitCell(
          this.add.text(col[h.key], startY - 18, h.str, fontStyle(FONT_SIZE.CAPTION, "#8B5E3C")),
          colW[h.key],
        );
        this.lbGroup.add(cell);
      });
    }

    data.leaderboard.forEach((entry, i) => {
      const y = startY + i * 22;
      const color = i < 3 ? medalColors[i] : "#FFFFFF";
      const cells = [
        { key: "rank", str: `#${entry.rank}`, fill: color },
        {
          key: "name",
          str: entry.handle || entry.name || "???",
          fill: "#F5E6C8",
        },
        {
          key: "score",
          str: `${entry.score.toLocaleString()}`,
          fill: "#7BC74A",
        },
        { key: "time", str: formatTime(entry.timeSeconds), fill: "#666666" },
      ];

      cells.forEach((c) => {
        const cell = this._fitCell(
          this.add.text(
            col[c.key],
            y,
            c.str,
            fontStyle(FONT_SIZE.CAPTION, c.fill),
          ),
          colW[c.key],
        );
        this.lbGroup.add(cell);
      });
    });

    // Close button
    const closeBtn = this._fit(
      this.add
        .text(CX, H - 40, "CLOSE", fontStyle(FONT_SIZE.BODY, "#FF4444"))
        .setOrigin(0.5),
    ).setInteractive({ useHandCursor: true });
    closeBtn.on("pointerdown", () => {
      if (this.lbGroup) {
        this.lbGroup.destroy(true);
        this.lbGroup = null;
      }
    });
    this.lbGroup.add(closeBtn);

    closeZone.on("pointerdown", () => {
      if (this.lbGroup) {
        this.lbGroup.destroy(true);
        this.lbGroup = null;
      }
    });
  }
}

export default DailyChallengeScene;
